import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  PhotoIcon,
  CheckCircleIcon,
  XMarkIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const TABS = [
  { key: 'lost_found', label: 'Lost & Found', icon: MagnifyingGlassIcon, closeLabel: 'Mark Found' },
  { key: 'maintenance', label: 'Maintenance', icon: WrenchScrewdriverIcon, closeLabel: 'Mark Resolved' }
];

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const FacilityDetail = () => {
  const { facilityId } = useParams();
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [facility, setFacility] = useState(null);
  const [resources, setResources] = useState([]);
  const [permissions, setPermissions] = useState({ canUseLostFound: false, canCreateMaintenance: false, isOrgAdmin: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [activeTab, setActiveTab] = useState('lost_found');
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ title: '', description: '', facilityResourceId: '', photo: null });
  const [submitting, setSubmitting] = useState(false);

  const [selectedReportId, setSelectedReportId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyPhoto, setReplyPhoto] = useState(null);
  const [replySubmitting, setReplySubmitting] = useState(false);

  const fetchFacility = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await axios.get(`/api/facility-reports/facility/${facilityId}`, { headers: authHeaders });
      setFacility(res.data.facility);
      setResources(res.data.resources || []);
      setPermissions(res.data.permissions || {});
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Failed to load facility');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  const fetchReports = useCallback(async (type) => {
    try {
      setReportsLoading(true);
      const res = await axios.get('/api/facility-reports', {
        params: { facilityId, type },
        headers: authHeaders
      });
      setReports(res.data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  useEffect(() => { fetchFacility(); }, [fetchFacility]);
  useEffect(() => {
    if (facility) fetchReports(activeTab);
  }, [facility, activeTab, fetchReports]);

  const canCreateForActiveTab = activeTab === 'lost_found' ? permissions.canUseLostFound : permissions.canCreateMaintenance;
  const showMaintenanceTab = permissions.canCreateMaintenance || permissions.isOrgAdmin;

  const openThread = async (reportId) => {
    setSelectedReportId(reportId);
    setThread(null);
    setThreadLoading(true);
    try {
      const res = await axios.get(`/api/facility-reports/${reportId}`, { headers: authHeaders });
      setThread(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load report');
      setSelectedReportId(null);
    } finally {
      setThreadLoading(false);
    }
  };

  const closeThread = () => {
    setSelectedReportId(null);
    setThread(null);
    setReplyContent('');
    setReplyPhoto(null);
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    if (!newForm.title.trim()) {
      toast.error('Please enter a title.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('facilityId', facilityId);
      formData.append('reportType', activeTab);
      formData.append('title', newForm.title.trim());
      if (newForm.description.trim()) formData.append('description', newForm.description.trim());
      if (newForm.facilityResourceId) formData.append('facilityResourceId', newForm.facilityResourceId);
      if (newForm.photo) formData.append('photo', newForm.photo);

      await axios.post('/api/facility-reports', formData, { headers: authHeaders });
      toast.success(activeTab === 'lost_found' ? 'Item reported.' : 'Maintenance issue logged.');
      setShowNewForm(false);
      setNewForm({ title: '', description: '', facilityResourceId: '', photo: null });
      fetchReports(activeTab);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() && !replyPhoto) {
      toast.error('Add a message or a photo.');
      return;
    }
    setReplySubmitting(true);
    try {
      const formData = new FormData();
      if (replyContent.trim()) formData.append('content', replyContent.trim());
      if (replyPhoto) formData.append('photo', replyPhoto);

      await axios.post(`/api/facility-reports/${selectedReportId}/messages`, formData, { headers: authHeaders });
      setReplyContent('');
      setReplyPhoto(null);
      openThread(selectedReportId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleClose = async (reportId) => {
    try {
      await axios.put(`/api/facility-reports/${reportId}/close`, {}, { headers: authHeaders });
      toast.success(activeTab === 'lost_found' ? 'Marked found.' : 'Marked resolved.');
      fetchReports(activeTab);
      if (selectedReportId === reportId) closeThread();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close report');
    }
  };

  const canCloseReport = (report) => {
    if (report.reportType === 'lost_found') return permissions.canUseLostFound;
    return permissions.isOrgAdmin;
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading facility...</div>;
  }

  if (loadError || !facility) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-700">{loadError || 'Facility not found.'}</p>
        <Link to="/dashboard" className="mt-4 inline-flex text-sm font-semibold text-indigo-600 hover:underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const activeTabDef = TABS.find(t => t.key === activeTab);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="h-4 w-4" /> Back
      </Link>

      <div className="mt-3 flex items-start gap-3">
        <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
          <MapPinIcon className="h-6 w-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{facility.name}</h1>
          {facility.address && (
            <p className="text-sm text-gray-500">
              {[facility.address.city, facility.address.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.filter(t => t.key === 'lost_found' || showMaintenanceTab).map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setShowNewForm(false); }}
                className={`flex items-center gap-2 border-b-2 py-3 text-sm font-semibold ${
                  activeTab === tab.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" /> {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {activeTab === 'lost_found'
            ? 'Anyone in the organization can see and reply to these.'
            : 'Only visible to you and organization admins.'}
        </p>
        {canCreateForActiveTab && (
          <button
            onClick={() => setShowNewForm(v => !v)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            {showNewForm ? 'Cancel' : activeTab === 'lost_found' ? '+ Report Item' : '+ Log Issue'}
          </button>
        )}
      </div>

      {showNewForm && (
        <form onSubmit={handleCreateReport} className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600">Title</label>
            <input
              type="text"
              value={newForm.title}
              onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))}
              placeholder={activeTab === 'lost_found' ? 'e.g. Black batting gloves' : 'e.g. Net on Field 3 is ripped'}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600">Details</label>
            <textarea
              rows={3}
              value={newForm.description}
              onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>
          {resources.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-600">Field / area (optional)</label>
              <select
                value={newForm.facilityResourceId}
                onChange={e => setNewForm(f => ({ ...f, facilityResourceId: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              >
                <option value="">Whole facility</option>
                {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600">Photo (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setNewForm(f => ({ ...f, photo: e.target.files?.[0] || null }))}
              className="mt-1 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {reportsLoading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : reports.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            Nothing here right now.
          </div>
        ) : (
          reports.map(report => (
            <div key={report.id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => openThread(report.id)} className="flex-1 text-left">
                  <p className="font-semibold text-gray-900">{report.title}</p>
                  {report.facilityResourceName && (
                    <p className="text-xs text-gray-500">{report.facilityResourceName}</p>
                  )}
                  {report.description && (
                    <p className="mt-1 text-sm text-gray-600 line-clamp-2">{report.description}</p>
                  )}
                  <p className="mt-2 text-xs text-gray-400">
                    {report.reporter?.name || 'Unknown'} &middot; {formatDate(report.createdAt)}
                  </p>
                </button>
                {report.photoUrl && (
                  <img src={report.photoUrl} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
                )}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={() => openThread(report.id)}
                  className="text-sm font-semibold text-indigo-600 hover:underline"
                >
                  View / reply
                </button>
                {canCloseReport(report) && (
                  <button
                    onClick={() => handleClose(report.id)}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:underline"
                  >
                    <CheckCircleIcon className="h-4 w-4" /> {activeTabDef.closeLabel}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {selectedReportId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={closeThread}>
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-bold text-gray-900">{thread?.report?.title || 'Report'}</h2>
              <button onClick={closeThread}><XMarkIcon className="h-5 w-5 text-gray-400" /></button>
            </div>

            {threadLoading ? (
              <p className="mt-4 text-sm text-gray-500">Loading...</p>
            ) : thread ? (
              <>
                {thread.report.status === 'closed' && (
                  <p className="mt-2 inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {activeTab === 'lost_found' ? 'Found' : 'Resolved'}
                    {thread.report.closedBy ? ` by ${thread.report.closedBy.name}` : ''}
                  </p>
                )}
                {thread.report.description && (
                  <p className="mt-3 text-sm text-gray-700">{thread.report.description}</p>
                )}
                {thread.report.photoUrl && (
                  <img src={thread.report.photoUrl} alt="" className="mt-3 max-h-64 w-full rounded-lg object-cover" />
                )}
                <p className="mt-2 text-xs text-gray-400">
                  {thread.report.reporter?.name || 'Unknown'} &middot; {formatDate(thread.report.createdAt)}
                </p>

                <div className="mt-5 space-y-4 border-t border-gray-100 pt-4">
                  {thread.messages.length === 0 ? (
                    <p className="text-sm text-gray-400">No replies yet.</p>
                  ) : thread.messages.map(m => (
                    <div key={m.id}>
                      <p className="text-sm">
                        <span className="font-semibold text-gray-900">{m.user?.name || 'Unknown'}</span>{' '}
                        <span className="text-xs text-gray-400">{formatDate(m.createdAt)}</span>
                      </p>
                      {m.content && <p className="mt-0.5 text-sm text-gray-700">{m.content}</p>}
                      {m.photoUrl && <img src={m.photoUrl} alt="" className="mt-1 max-h-48 rounded-lg object-cover" />}
                    </div>
                  ))}
                </div>

                {thread.report.status === 'open' && (
                  <form onSubmit={handleReply} className="mt-5 border-t border-gray-100 pt-4 space-y-2">
                    <textarea
                      rows={2}
                      value={replyContent}
                      onChange={e => setReplyContent(e.target.value)}
                      placeholder="Write a reply..."
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                    />
                    <div className="flex items-center justify-between">
                      <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-gray-500">
                        <PhotoIcon className="h-4 w-4" />
                        {replyPhoto ? replyPhoto.name : 'Add photo'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={e => setReplyPhoto(e.target.files?.[0] || null)}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={replySubmitting}
                        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" /> Reply
                      </button>
                    </div>
                  </form>
                )}

                {thread.report.status === 'open' && canCloseReport({ reportType: thread.report.reportType }) && (
                  <button
                    onClick={() => handleClose(thread.report.id)}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 hover:underline"
                  >
                    <CheckCircleIcon className="h-4 w-4" /> {activeTabDef.closeLabel}
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityDetail;
